import React, { useState, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import EmojiPicker from 'emoji-picker-react';
import { Button } from './ui/button';
import { useTheme } from '../contexts/ThemeContext';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Quote,
  Undo,
  Redo,
  RemoveFormatting,
  Smile
} from 'lucide-react';

const MenuBar = ({ editor, onEmojiSelect }) => {
  const { isDarkMode } = useTheme();
  const [emojiOpen, setEmojiOpen] = useState(false);
  
  if (!editor) {
    return null;
  }

  const buttonClass = (isActive) => `
    h-8 w-8 p-0 
    ${isActive 
      ? (isDarkMode 
          ? 'bg-indigo-600 text-white' 
          : 'bg-indigo-100 text-indigo-700') 
      : (isDarkMode 
          ? 'text-slate-300 hover:bg-[hsl(220,14%,20%)]' 
          : 'text-slate-600 hover:bg-slate-100')
    }
  `;

  const handleEmojiClick = (emojiData) => {
    editor.chain().focus().insertContent(emojiData.emoji).run();
    setEmojiOpen(false);
  };

  return (
    <div className={`flex flex-wrap gap-1 p-2 border-b ${isDarkMode ? 'border-[hsl(220,14%,25%)] bg-[hsl(220,14%,16%)]' : 'border-slate-200 bg-slate-50'} rounded-t-lg`}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={buttonClass(editor.isActive('bold'))}
        title="Gras (Ctrl+B)"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={buttonClass(editor.isActive('italic'))}
        title="Italique (Ctrl+I)"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={buttonClass(editor.isActive('underline'))}
        title="Souligné (Ctrl+U)"
      >
        <UnderlineIcon className="h-4 w-4" />
      </Button>
      
      <div className={`w-px h-6 mx-1 ${isDarkMode ? 'bg-[hsl(220,14%,25%)]' : 'bg-slate-300'}`} />
      
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={buttonClass(editor.isActive('heading', { level: 1 }))}
        title="Titre 1"
      >
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={buttonClass(editor.isActive('heading', { level: 2 }))}
        title="Titre 2"
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      
      <div className={`w-px h-6 mx-1 ${isDarkMode ? 'bg-[hsl(220,14%,25%)]' : 'bg-slate-300'}`} />
      
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={buttonClass(editor.isActive('bulletList'))}
        title="Liste à puces"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={buttonClass(editor.isActive('orderedList'))}
        title="Liste numérotée"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={buttonClass(editor.isActive('blockquote'))}
        title="Citation"
      >
        <Quote className="h-4 w-4" />
      </Button>
      
      <div className={`w-px h-6 mx-1 ${isDarkMode ? 'bg-[hsl(220,14%,25%)]' : 'bg-slate-300'}`} />
      
      {/* Emoji Picker */}
      <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={buttonClass(false)}
            title="Insérer un emoji"
          >
            <Smile className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" side="bottom">
          <EmojiPicker 
            onEmojiClick={handleEmojiClick}
            width={320}
            height={400}
            searchPlaceHolder="Rechercher un emoji..."
            previewConfig={{ showPreview: false }}
            skinTonesDisabled
            theme={isDarkMode ? 'dark' : 'light'}
            lazyLoadEmojis
          />
        </PopoverContent>
      </Popover>
      
      <div className={`w-px h-6 mx-1 ${isDarkMode ? 'bg-[hsl(220,14%,25%)]' : 'bg-slate-300'}`} />
      
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        className={buttonClass(false)}
        title="Supprimer la mise en forme"
      >
        <RemoveFormatting className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className={buttonClass(false)}
        title="Annuler (Ctrl+Z)"
      >
        <Undo className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className={buttonClass(false)}
        title="Rétablir (Ctrl+Y)"
      >
        <Redo className="h-4 w-4" />
      </Button>
    </div>
  );
};

const RichTextEditor = ({ 
  content = '', 
  onChange, 
  placeholder = 'Écrivez vos instructions ici...',
  minHeight = '150px'
}) => {
  const { isDarkMode } = useTheme();
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
      Underline,
    ],
    content,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none p-4 min-h-[${minHeight}] ${
          isDarkMode 
            ? 'prose-invert text-slate-200' 
            : 'text-slate-800'
        }`,
      },
    },
  });

  return (
    <div className={`rounded-lg border ${isDarkMode ? 'border-[hsl(220,14%,25%)] bg-[hsl(220,14%,18%)]' : 'border-slate-200 bg-white'}`}>
      <MenuBar editor={editor} />
      <EditorContent 
        editor={editor} 
        className={`
          [&_.ProseMirror]:min-h-[${minHeight}] 
          [&_.ProseMirror]:p-4
          [&_.is-editor-empty]:before:content-[attr(data-placeholder)]
          [&_.is-editor-empty]:before:float-left
          [&_.is-editor-empty]:before:h-0
          [&_.is-editor-empty]:before:pointer-events-none
          ${isDarkMode 
            ? '[&_.is-editor-empty]:before:text-slate-500' 
            : '[&_.is-editor-empty]:before:text-slate-400'
          }
        `}
      />
      <style>{`
        .ProseMirror {
          min-height: ${minHeight};
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: ${isDarkMode ? 'rgb(100, 116, 139)' : 'rgb(148, 163, 184)'};
          pointer-events: none;
          height: 0;
        }
        .ProseMirror h1 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }
        .ProseMirror h2 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .ProseMirror ul {
          list-style-type: disc;
        }
        .ProseMirror ol {
          list-style-type: decimal;
        }
        .ProseMirror blockquote {
          border-left: 3px solid ${isDarkMode ? 'rgb(99, 102, 241)' : 'rgb(79, 70, 229)'};
          padding-left: 1rem;
          margin: 0.5rem 0;
          font-style: italic;
          color: ${isDarkMode ? 'rgb(148, 163, 184)' : 'rgb(100, 116, 139)'};
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
